// client/src/components/documentos/RecibosHistoricoPage.js
import React, { Fragment, useState } from 'react';
import { Receipt, Eye, Loader2, X, Thermometer, Printer } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import { useRecibosHistorico, useReciboHistoricoById } from '../../hooks/useRecibosHistorico';
import { wrapRecibo, abrirJanelaImpressao, LARGURAS_TERMICA } from '../../utils/reciboPrintLayouts';
import useAlert from '../../hooks/useAlert';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';

const fmtData = (data) => (data ? new Date(data).toLocaleString('pt-BR') : '—');

const getClienteNome = (cliente) => {
    if (!cliente || typeof cliente !== 'object') return '—';
    return cliente.user?.name || '—';
};

const ReciboPreviewModal = ({ reciboId, onClose }) => {
    const { data: recibo, isLoading } = useReciboHistoricoById(reciboId);
    const { setAlert } = useAlert();
    const [layout, setLayout] = useState('a4');
    const [larguraTermica, setLarguraTermica] = useState(80);

    const handleImprimir = () => {
        if (!recibo?.htmlGerado) return;
        const html = wrapRecibo(recibo.htmlGerado, layout, recibo.templateNome, larguraTermica);
        const abriu = abrirJanelaImpressao(html, layout === 'termica' ? larguraTermica : null);
        if (!abriu) {
            setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
        }
    };

    const previewHtml = recibo?.htmlGerado
        ? wrapRecibo(recibo.htmlGerado, layout, recibo.templateNome, larguraTermica)
        : null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 820, width: '100%',
                    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3 className="form-title" style={{ margin: 0 }}>
                        {recibo?.templateNome || 'Recibo'}
                    </h3>
                    <button onClick={onClose} className="form-close-button" title="Fechar">
                        <X size={20} />
                    </button>
                </div>

                {isLoading ? (
                    <div className="table-loading-state">
                        <Loader2 size={24} className="animate-spin" />
                        <span>Carregando recibo...</span>
                    </div>
                ) : recibo ? (
                    <Fragment>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                            <button
                                type="button"
                                onClick={() => setLayout((prev) => (prev === 'a4' ? 'termica' : 'a4'))}
                                className="action-button action-button-secondary"
                            >
                                <Thermometer size={16} className="icon" />
                                {layout === 'a4' ? 'Modo Térmica' : 'Modo A4'}
                            </button>

                            {layout === 'termica' && (
                                <select
                                    value={larguraTermica}
                                    onChange={(e) => setLarguraTermica(Number(e.target.value))}
                                    className="form-input"
                                    style={{ width: 'auto', padding: '0.5rem 0.75rem' }}
                                >
                                    {LARGURAS_TERMICA.map((opt) => (
                                        <option key={opt.valor} value={opt.valor}>{opt.label}</option>
                                    ))}
                                </select>
                            )}

                            <button
                                type="button"
                                onClick={handleImprimir}
                                className="action-button action-button-secondary"
                                disabled={!recibo.htmlGerado}
                            >
                                <Printer size={16} className="icon" />
                                Imprimir
                            </button>
                        </div>

                        {previewHtml ? (
                            <div
                                style={{
                                    border: '1px solid #e5e7eb', borderRadius: 8, background: '#f3f4f6',
                                    padding: '1rem', display: 'flex', justifyContent: 'center', overflowX: 'auto',
                                }}
                            >
                                <iframe
                                    key={`${layout}-${larguraTermica}`}
                                    title="Preview do recibo"
                                    srcDoc={previewHtml}
                                    sandbox="allow-same-origin"
                                    style={{
                                        border: '1px solid #d1d5db',
                                        background: '#fff',
                                        width: layout === 'termica' ? `${larguraTermica}mm` : '210mm',
                                        height: layout === 'termica' ? '600px' : '700px',
                                        maxWidth: '100%',
                                    }}
                                />
                            </div>
                        ) : (
                            <p className="text-muted">Recibo sem conteúdo gerado.</p>
                        )}
                    </Fragment>
                ) : (
                    <p className="text-muted">Recibo não encontrado.</p>
                )}
            </div>
        </div>
    );
};

const RecibosHistoricoPage = () => {
    const { data: recibos = [], isLoading } = useRecibosHistorico();
    const [previewId, setPreviewId] = useState(null);
    // ✅ ACHADO: fmtMoney era fixo em BRL, sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();
    const fmtMoney = (valor) =>
        typeof valor === 'number' ? formatCurrencyUtil(valor, currency, locale) : '—';

    return (
        <Fragment>
            {previewId && <ReciboPreviewModal reciboId={previewId} onClose={() => setPreviewId(null)} />}

            <div className="page-management-container">
                <PageHeader
                    icon={<Receipt />}
                    title="Histórico de Recibos"
                    subtitle={!isLoading ? `${recibos.length} recibo${recibos.length !== 1 ? 's' : ''}` : null}
                />
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando recibos...</span>
                        </div>
                    ) : recibos.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Cliente</th>
                                    <th>Template</th>
                                    <th>Valor</th>
                                    <th>Emitido em</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recibos.map((recibo) => (
                                    <tr key={recibo._id}>
                                        <td style={{ textAlign: 'left' }}>{getClienteNome(recibo.cliente)}</td>
                                        <td>{recibo.templateNome || '—'}</td>
                                        <td>{fmtMoney(recibo.valor)}</td>
                                        <td style={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                            {fmtData(recibo.createdAt)}
                                        </td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => setPreviewId(recibo._id)}
                                                className="action-button-icon text-blue-600"
                                                title="Visualizar recibo"
                                            >
                                                <Eye size={18} className="icon" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Receipt size={48} />
                            <h3>Nenhum recibo emitido</h3>
                            <p>Recibos gerados a partir de um agendamento aparecerão aqui.</p>
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default RecibosHistoricoPage;
